import React, { useState, useRef } from 'react';
import { Upload, X, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

interface Props {
  applicationId: number;
  onUploadComplete?: () => void;
}

interface UploadedFile {
  name: string;
  size: number;
  type: string;
  uploading: boolean;
  progress: number;
  error?: string;
  success?: boolean;
}

const DOCUMENT_CATEGORIES = [
  { value: 'Dokumenty zawodowe', label: '📄 Dokumenty zawodowe' },
  { value: 'Wykształcenie i uprawnienia', label: '🎓 Wykształcenie i uprawnienia' },
  { value: 'Certyfikaty i szkolenia', label: '📜 Certyfikaty i szkolenia' },
  { value: 'Oferty na zakupy', label: '🛒 Oferty na zakupy' },
  { value: 'Portfolio', label: '💼 Portfolio' },
  { value: 'Dodatkowe dokumenty', label: '📎 Dodatkowe dokumenty' },
  { value: 'Poręczenia', label: '🤝 Poręczenia' },
  { value: 'Wniosek w instytucji finansującej', label: '🏦 Wniosek w instytucji finansującej' },
];

export const FileUploadPanel: React.FC<Props> = ({ applicationId, onUploadComplete }) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(DOCUMENT_CATEGORIES[5].value);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles: UploadedFile[] = Array.from(selectedFiles).map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      uploading: false,
      progress: 0,
    }));

    setFiles(prev => [...prev, ...newFiles]);

    // Upload each file
    Array.from(selectedFiles).forEach((file, index) => {
      uploadFile(file, files.length + index);
    });
  };

  const uploadFile = async (file: File, fileIndex: number) => {
    setFiles(prev => {
      const updated = [...prev];
      updated[fileIndex] = { ...updated[fileIndex], uploading: true, progress: 0 };
      return updated;
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('applicationId', applicationId.toString());
    formData.append('category', selectedCategory);
    formData.append('description', `Uploaded: ${file.name}`);

    try {
      const token = localStorage.getItem('grant_crm_token');

      await axios.post('/api/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;

          setFiles(prev => {
            const updated = [...prev];
            updated[fileIndex] = { ...updated[fileIndex], progress };
            return updated;
          });
        },
      });

      setFiles(prev => {
        const updated = [...prev];
        updated[fileIndex] = { ...updated[fileIndex], uploading: false, progress: 100, success: true };
        return updated;
      });

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || 'Błąd podczas uploadu';

      setFiles(prev => {
        const updated = [...prev];
        updated[fileIndex] = { ...updated[fileIndex], uploading: false, error: errorMessage };
        return updated;
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Upload className="w-5 h-5" />
        Upload Załączników
      </h3>

      {/* Category Selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Kategoria dokumentu
        </label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {DOCUMENT_CATEGORIES.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      {/* Drag & Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
        <p className="text-gray-700 font-medium mb-2">
          Przeciągnij pliki tutaj lub kliknij aby wybrać
        </p>
        <p className="text-sm text-gray-500 mb-1">
          Dozwolone: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX (max 50MB)
        </p>
        <p className="text-xs text-gray-400 mb-4">
          Dla lepszego OCR: używaj PDF tekstowych lub wyraźnych skanów (min. 150 DPI)
        </p>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
        >
          Wybierz Pliki
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Files List */}
      {files.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="font-semibold text-gray-800">Pliki ({files.length})</h4>
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <FileText className="w-8 h-8 text-blue-600 flex-shrink-0" />

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-medium text-gray-900 truncate">{file.name}</span>
                  <span className="text-sm text-gray-500 flex-shrink-0">
                    {formatFileSize(file.size)}
                  </span>
                </div>

                {file.uploading && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                )}

                {file.error && (
                  <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                    <AlertCircle className="w-4 h-4" />
                    {file.error}
                  </p>
                )}

                {file.success && (
                  <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                    <CheckCircle className="w-4 h-4" />
                    Upload ukończony
                  </p>
                )}
              </div>

              {!file.uploading && (
                <button
                  onClick={() => removeFile(index)}
                  className="text-gray-400 hover:text-red-600 transition flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
